import { App as CapacitorApp } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { Keyboard, KeyboardResize } from '@capacitor/keyboard'
import { SplashScreen } from '@capacitor/splash-screen'
import { SafeArea } from 'capacitor-plugin-safe-area'
import { register as registerSwiper } from 'swiper/element'
import { createApp } from 'vue'

import {
  ConsoleTracker,
  InternalTracker,
  LDialogPlugin,
  LNotificationPlugin,
  MultiTracker,
  PosthogTracker,
  trackerPlugin
} from '@last/core-ui/paprika'
import { vHorizontalScroll } from '@last/core-ui/paprika/directives'

import AppRoot from '@/AppRoot.vue'
import i18n from '@/i18n'
import jobs from '@/jobs'
import localDb from '@/localDb'
import { ManagerConfirmationPlugin } from '@/plugins/managerConfirmation'
import { router } from '@/router'
import pinia from '@/store'
import { useAuthStore } from '@/store/auth'
import { useBeepersStore } from '@/store/beepers'
import { useCatalogStore } from '@/store/catalog'
import { useConfigStore } from '@/store/config'
import { useCouriersStore } from '@/store/couriers'
import { usePromotionsStore } from '@/store/promotions'
import { useStatusStore } from '@/store/status'
import { useTabsStore } from '@/store/tabs'
import sync from '@/sync/service'

import App from './app'
import { rum } from './monitoring'
import { CashMachineStatusPlugin } from './plugins/cashMachineStatus'
import { UpdateModalPlugin } from './plugins/updateVersion'
import { isMobile } from './utils/isMobile'
import { isSemverGreater } from './utils/semver'

import './style.css'

if (Capacitor.getPlatform() === 'ios') {
  Keyboard.setResizeMode({ mode: KeyboardResize.Native })
}

const app = createApp(AppRoot)

app.config.performance = import.meta.env.DEV

app.directive('horizontal-scroll', vHorizontalScroll)
app.use(pinia)
app.use(i18n)
app.use(router)
app.use(ManagerConfirmationPlugin)
app.use(CashMachineStatusPlugin)
app.use(UpdateModalPlugin)
app.use(LDialogPlugin)
app.use(LNotificationPlugin)

rum.setRouter(router)
if (import.meta.env.PROD) {
  app.config.errorHandler = err => {
    rum.addError(err)
  }
}

const trackers = []

if (import.meta.env.VITE_POSTHOG_API_KEY) {
  trackers.push(new PosthogTracker(import.meta.env.VITE_POSTHOG_API_KEY))
}
if (import.meta.env.VITE_NODE_ENV === 'development') {
  trackers.push(new ConsoleTracker())
}

trackers.push(new InternalTracker(import.meta.env.VITE_APP_BASE_URL))

app.use(trackerPlugin, {
  tracker: new MultiTracker(trackers)
})

registerSwiper()

app.mount('#app')

async function init() {
  App.notifyAppReady()
  const authStore = useAuthStore(pinia)
  const configStore = useConfigStore(pinia)
  const tabsStore = useTabsStore(pinia)
  const catalogStore = useCatalogStore(pinia)
  const statusStore = useStatusStore(pinia)
  const promotionsStore = usePromotionsStore(pinia)
  const couriersStore = useCouriersStore(pinia)
  const beepersStore = useBeepersStore(pinia)
  if (!sync.isInitialized()) {
    const tabKeys = Object.keys(tabsStore).map(key => `tabs:${key}`)
    let tabsTables = await localDb.loadTables(tabKeys)
    tabsTables = Object.keys(tabsTables).reduce((res, key) => {
      res[key.replace('tabs:', '')] = tabsTables[key]
      return res
    }, {} as any)
    if (!sync.isInitialized()) {
      // Avoid race condition with sync
      tabsStore.replaceTabs(tabsTables)
    }
  }
  if (authStore.locationId) {
    statusStore.updateCurrentStatus()
    catalogStore.refreshCatalogs()
    configStore.refreshConfig()
    authStore.refreshCurrentLocation()
    statusStore.refreshErrorMessages()
    authStore.refreshBillingStatus()
    beepersStore.refreshBeepers()
    promotionsStore.refreshPromotions()
    couriersStore.refreshCouriers()
  }
  CapacitorApp.addListener('appStateChange', state => {
    if (state.isActive && authStore.isAuthenticated) {
      sync.resync()
      configStore.refreshConfig()
      catalogStore.refreshCatalogs()
      authStore.refreshEmployees()
      statusStore.refreshErrorMessages()
      beepersStore.refreshBeepers()
      promotionsStore.refreshPromotions()
      couriersStore.refreshCouriers()
    }
  })
  jobs.start()
  SplashScreen.hide()
}

/**
 * We use immersive mode and we need to set the safe area insets to the root element.
 * The keyboard on Android also changes the safe area insets, so we need to update them when the keyboard is shown or hidden.
 */
async function setupSafeArea() {
  if (!['android', 'ios'].includes(Capacitor.getPlatform())) return

  const deviceInfo = await App.getDeviceInfo()
  if (
    Capacitor.getPlatform() === 'android' &&
    !isMobile.value &&
    deviceInfo.nativeVersion &&
    isSemverGreater(deviceInfo.nativeVersion, '2.3.2')
  ) {
    return
  }

  const updateProperties = async () => {
    const { insets } = await SafeArea.getSafeAreaInsets()
    for (const [key, value] of Object.entries(insets)) {
      document.documentElement.style.setProperty(
        `--safe-area-inset-${key}`,
        `max(env(safe-area-inset-${key}), ${value}px)`
      )
    }
  }

  updateProperties()
  SafeArea.addListener('safeAreaChanged', () => updateProperties())
  Keyboard.addListener('keyboardWillShow', () => {
    updateProperties()
    document.documentElement.classList.add('keyboard-shown')
  })
  Keyboard.addListener('keyboardWillHide', () => {
    updateProperties()
    document.documentElement.classList.remove('keyboard-shown')
  })
}

if (window.location.href.includes('refreshLocalStorage')) {
  localStorage.clear()
}

setupSafeArea()
init()
